import { identity, pick } from 'lodash';
import type { ActionErrorJson } from '@/backend/actions/test-helpers/createActionErrorFromJson';
import type { JsonObject } from '@/backend/entities/JsonObject';
import { dummyFormatter } from '@/backend/errors/dummyFormatter';
import type { Formatter } from '@/backend/errors/Formatter';
import { joinAndIndent } from '@/backend/logging/joinAndIndent';

export type InnerErrorJsonType = JsonObject | JsonObject[] | string | null;

export type InnerErrorType = Error | Error[] | null;

export interface ActionErrorDetails {
  path: string[];
  description: string;
  problemCode: string;
  httpStatusCode: number;
}

export class ActionError extends Error {
  constructor(
    public readonly description: string,
    public readonly innerException: Error | Error[] | null = null,
    public readonly problemCode: string = 'ActionError',
    public readonly httpStatusCode: number = 500,
  ) {
    super(`[${httpStatusCode}] ${description}`, { cause: innerException });

    this.name = problemCode;
    this.kind = problemCode;
  }

  kind: string;

  path: string[] = [];

  format(printer: Formatter = dummyFormatter) {
    const selfMessage = this.printSelf(printer);

    let innerMessage = '';
    if (this.innerException) {
      const exceptions = Array.isArray(this.innerException)
        ? this.innerException
        : [this.innerException];
      innerMessage = joinAndIndent(exceptions, '  ', printer);
    }

    return `${selfMessage}${innerMessage}`;
  }

  printSelf(printer: Formatter) {
    return [
      this.printMessage(printer),
      this.printDetails(printer),
      this.printStack(printer),
    ]
      .filter(identity)
      .join('\n');
  }

  printMessage(_printer: Formatter) {
    if (!this.stack) {
      return this.toString();
    }

    const [message] = this.stack.split('\n');

    return message;
  }

  printDetails(printer: Formatter) {
    return Object.entries(this.details())
      .map(([key, value]) => `${key}: ${printer.dimmed(JSON.stringify(value))}`)
      .join('\n');
  }

  printStack(printer: Formatter) {
    if (!this.stack) {
      return '';
    }

    const [, ...stack] = this.stack.split('\n');

    return printer.dimmed(stack.join('\n'));
  }

  /**
   * Computes details to return to sentry.
   *
   * @returns Details which should be included in the error object sent to sentry
   */
  details(): ActionErrorDetails {
    return {
      path: this.path,
      description: this.description,
      problemCode: this.problemCode,
      httpStatusCode: this.httpStatusCode,
    };
  }

  directCause() {
    return Array.isArray(this.innerException)
      ? this.innerException[0]
      : this.innerException;
  }

  /**
   *
   * @returns Details which should allow grouping this error with other similar errors.
   */
  selfFingerprint() {
    return [
      ...this.path,
      `${this.httpStatusCode}`,
      this.problemCode,
      this.description,
    ];
  }

  /**
   * Computes a fingerprint for this error by concatenating the fingerprints of this and all inner errors.
   *
   * Customize with selfFingerprint.
   *
   * @returns A list of strings which will be hashed by sentry to determine which error group to include this error in.
   */
  fingerprint(): string[] {
    const inner = this.directCause();
    const innerFingerprint = getInnerFingerprint(inner);
    return [...this.selfFingerprint(), ...innerFingerprint];
  }

  toJSON(): ActionErrorJson {
    return {
      ...pick(this, 'message', 'stack'),
      ...this.details(),
      inner: ActionError.innerToJson(this.innerException),
    };
  }

  static innerToJson(inner: InnerErrorType): InnerErrorJsonType {
    if (inner == null) {
      return inner;
    }
    if (Array.isArray(inner)) {
      return inner.map(this.innerToJson) as JsonObject[];
    }

    if (inner instanceof ActionError) {
      return inner.toJSON();
    }

    return inner.stack ? inner.stack + '' : inner + '';
  }
}

function getInnerFingerprint(inner: Error | null): string[] {
  if (!inner) {
    return [];
  }
  if ('fingerprint' in inner && typeof inner.fingerprint === 'function') {
    return inner.fingerprint() as string[];
  }
  return [inner.name];
}
